import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Navbar, Container, Nav, NavDropdown, Button } from 'react-bootstrap';
import { signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { useTheme } from '../utils/ThemeContext';
import logo from '../logo.png';
import ProfileModal from './ProfileModal';

import './Header.css';

/**
 * Main navigation header component with responsive design and theme support
 * 
 * @param {Object} props - Component props
 * @param {Object} props.user - Current user object from Firebase Auth
 * @returns {JSX.Element} Rendered Header component
 */
const Header = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme, toggleTheme } = useTheme();
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState('');
  
  // Check if the current route is the login page
  const isLoginPage = location.pathname === '/login';
  
  // Fetch profile photo from userPreferences
  useEffect(() => {
    if (user) {
      // Start with the photoURL from Firebase Auth (e.g. Google profile photo)
      setProfilePhotoUrl(user.photoURL || '');
      
      // Then check if there's a custom profilePhotoUrl in Firestore
      const fetchUserPreferences = async () => {
        try {
          const userPrefsRef = doc(db, "userPreferences", user.uid);
          const userPrefsSnap = await getDoc(userPrefsRef);
          
          if (userPrefsSnap.exists()) {
            const prefs = userPrefsSnap.data();
            // Override with custom photo if it exists
            if (prefs.profilePhotoUrl) {
              setProfilePhotoUrl(prefs.profilePhotoUrl);
            }
          }
        } catch (error) {
          console.error('Error fetching user preferences:', error);
        }
      };
      
      fetchUserPreferences();
    }
  }, [user]);
  
  /**
   * Handle user sign out
   */
  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Don't show header on login page
  if (isLoginPage) return null;

  return (
    <>
      <Navbar 
        expand="lg" 
        className="header-navbar" 
        bg={theme === 'light' ? 'light' : 'dark'}
        variant={theme === 'light' ? 'light' : 'dark'}
        sticky="top"
      >
        <Container fluid>
          <div className="d-flex align-items-center">
            <Navbar.Brand as={Link} to="/" className="me-2">
              <h1 className="header-title mb-0">
                Moto<span className="tools-text">Tools</span>
              </h1>
            </Navbar.Brand>
            <div className="powered-by">
              <small>powered by</small>
              <a href="https://okinvent.net" target="_blank" rel="noopener noreferrer">
                <img src={logo} alt="Company Logo" className="company-logo" />
              </a>
            </div>
          </div>
          
          <Navbar.Toggle aria-controls="basic-navbar-nav" />
          
          <Navbar.Collapse id="basic-navbar-nav">
            <Nav className="me-auto">
              
            </Nav>
            
            <Nav>
              <Button 
                variant={theme === 'light' ? 'outline-dark' : 'outline-light'} 
                size="sm"
                onClick={toggleTheme}
                className="me-2"
              >
                <i className={`bi ${theme === 'light' ? 'bi-moon' : 'bi-sun'}`}></i>
                <span className="d-none d-md-inline ms-1">
                  {theme === 'light' ? 'Dark' : 'Light'}
                </span>
              </Button>
              
              {user ? (
                <NavDropdown 
                  title={
                    <div className="d-inline-flex align-items-center">
                      {profilePhotoUrl ? (
                        <img 
                          src={profilePhotoUrl} 
                          alt={user.displayName || user.email} 
                          className="profile-image me-1"
                        />
                      ) : (
                        <i className="bi bi-person-circle me-1"></i>
                      )}
                      <span className="d-none d-lg-inline">
                        {user.displayName || user.email.split('@')[0]}
                      </span>
                    </div>
                  } 
                  id="user-dropdown"
                  align="end"
                >
                  <NavDropdown.Item onClick={() => setIsProfileModalOpen(true)}>Profile</NavDropdown.Item>
                  <NavDropdown.Divider />
                  <NavDropdown.Item onClick={handleSignOut}>
                    <i className="bi bi-box-arrow-right me-2"></i>
                    Sign Out
                  </NavDropdown.Item>
                </NavDropdown>
              ) : (
                <Nav.Link as={Link} to="/login">
                  <i className="bi bi-box-arrow-in-right me-1"></i>
                  Sign In
                </Nav.Link>
              )}
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      {/* Profile Modal */}
      {user && (
        <ProfileModal 
          isOpen={isProfileModalOpen}
          onClose={() => setIsProfileModalOpen(false)}
          user={user}
        />
      )}
    </>
  );
};

export default Header;